<script setup lang="ts">
import { computed } from 'vue'

import AppIcon from '@/components/AppIcon.vue'
import type { NavApp } from '@/nav/apps'

/**
 * An app's icon: a white glyph on a tinted, top-lit squircle — the shape a
 * phone home screen has taught everyone to read as "an app you can open".
 *
 * Purely presentational and decorative: the app's name is always printed next
 * to or under the tile by whoever places it, so the tile itself is
 * `aria-hidden` and never the only content of a control.
 *
 * CLAUDE.md §11 reserves colour for state and bans decorative gradients; the
 * launcher is the one deliberate exception (DECISIONS.md 2026-10-11) — here
 * the hue IS the information, the fastest way to tell eighteen apps apart,
 * and the gradient is the lighting that makes a flat square read as a tile.
 */

const props = withDefaults(
  defineProps<{
    app: Pick<NavApp, 'glyph' | 'tint'>
    /** Edge length in px. 28 in menus, 36–40 in the sidebar header, 56–64 on the home grid. */
    size?: number
  }>(),
  { size: 40 },
)

// eslint-disable-next-line no-restricted-syntax -- pixel size, not money
const glyphSize = computed(() => Math.round(props.size * 0.54))
// A thin stroke disappears on a small coloured tile; a thick one clots on a big one.
const stroke = computed(() => (props.size <= 32 ? 2.25 : props.size <= 44 ? 2 : 1.85))

const style = computed(() => ({
  '--tile-size': `${props.size}px`,
  '--tile-from': props.app.tint.from,
  '--tile-to': props.app.tint.to,
}))
</script>

<template>
  <span class="app-tile" :style="style" aria-hidden="true">
    <AppIcon :name="app.glyph" :size="glyphSize" :stroke-width="stroke" class="app-tile__glyph" />
  </span>
</template>

<style>
.app-tile {
  position: relative;
  display: inline-grid;
  flex: none;
  place-items: center;
  inline-size: var(--tile-size);
  block-size: var(--tile-size);
  overflow: hidden;
  /* ≈ the continuous-corner radius of an iOS icon (22.37% of the edge). */
  border-radius: 22.5%;
  background: linear-gradient(180deg, var(--tile-from) 0%, var(--tile-to) 100%);
  color: #ffffff;
  box-shadow:
    inset 0 0 0 0.5px rgb(255 255 255 / 18%),
    0 1px 1.5px rgb(15 23 42 / 12%),
    0 3px 8px -2px color-mix(in srgb, var(--tile-to) 45%, transparent);
}

/* The top-light sheen. Behind the glyph, never over it. */
.app-tile::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgb(255 255 255 / 22%) 0%, rgb(255 255 255 / 0%) 52%);
  pointer-events: none;
}

.app-tile__glyph {
  position: relative;
  filter: drop-shadow(0 1px 1px rgb(0 0 0 / 18%));
}

@media (forced-colors: active) {
  .app-tile {
    border: 1px solid CanvasText;
  }
}
</style>
